import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { addDays, formatDate, formatMoney, formatPercent, isoDate } from '../lib/format';
import { P, useAuth } from '../lib/auth';
import { FormGrid, FormPage } from '../components/FormPage';
import { ReceiptPicker, useReceiptUpload } from '../components/ReceiptPicker';
import { Badge, Button, Detail, Field, Input, Select, Textarea, statusTone, useToast } from '../components/ui';
import { useClientOptions } from './Invoices';
import { useCurrencies } from './Clients';

interface ProjectOption {
  id: string;
  name: string;
  nameAr: string | null;
  code: string;
}

interface LineDraft {
  description: string;
  quantity: number;
  unitPrice: number;
}

/** What the invoice form starts from for one of a contract's periods (`GET /contracts/:id/invoice-draft`). */
interface ContractDraft {
  contract: { id: string; number: string; title: string; billing: string; currency: string };
  client: { id: string; code: string; companyName: string; companyNameAr: string | null };
  project: ProjectOption | null;
  period: { start: string; end: string; amount: number };
  currency: string;
  issueDate: string;
  dueDate: string;
  items: LineDraft[];
  clientInactive: boolean;
}

/**
 * CR-001 §46 — the invoice form, in the order the CR specifies: client first,
 * then the project (whose picker only loads once a client is chosen, §14), the
 * dates, the currency pre-filled from the client (§15), then the line items.
 *
 * No exchange rate: it moves from one payment to the next, so each payment
 * records its own (operator, 2026-10-09).
 *
 * Route: `/invoices/new`, optionally `?clientId=…` to arrive pre-filled.
 *
 * `?contractId=…&period=YYYY-MM-DD` — one of a contract's billing periods,
 * invoiced by hand (operator, 2026-10-27): the form starts from the contract
 * (`GET /contracts/:id/invoice-draft`) — its client, project and currency, which
 * stay the contract's, and one line at the period's suggested amount — and
 * nothing is saved until it is submitted, as a draft like any invoice.
 */
export function InvoiceFormPage() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clients = useClientOptions();
  const currencies = useCurrencies();
  const [searchParams] = useSearchParams();
  const contractId = searchParams.get('contractId');
  const period = searchParams.get('period');
  const forContract = !!contractId && !!period;

  // Read afresh each time: a period someone has just invoiced must not show as free.
  const draft = useQuery({
    queryKey: ['invoice-draft', contractId, period],
    queryFn: () => api.get<ContractDraft>(`/contracts/${contractId}/invoice-draft`, { period }),
    enabled: forContract,
    staleTime: 0,
    retry: false,
  });

  const [clientId, setClientId] = useState(searchParams.get('clientId') ?? '');
  const [projectId, setProjectId] = useState(searchParams.get('projectId') ?? '');
  const [currency, setCurrency] = useState('');
  const [issueDate, setIssueDate] = useState(isoDate());
  const [dueDate, setDueDate] = useState(addDays(isoDate(), 30));
  const [discountAmount, setDiscountAmount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<LineDraft[]>([{ description: '', quantity: 1, unitPrice: 0 }]);

  // A contract's period: the form starts from the contract, once.
  const [seeded, setSeeded] = useState(false);
  if (draft.data && !seeded) {
    setSeeded(true);
    setClientId(draft.data.client.id);
    setProjectId(draft.data.project?.id ?? '');
    setCurrency(draft.data.currency);
    setIssueDate(draft.data.issueDate);
    setDueDate(draft.data.dueDate);
    setItems(draft.data.items);
  }

  const client = (clients.data ?? []).find((c) => c.id === clientId);

  // §15 — the invoice takes the client's currency the moment one is chosen; a
  // contract's invoice keeps the contract's.
  useEffect(() => {
    if (client && !forContract) setCurrency(client.defaultCurrency);
  }, [client, forContract]);

  const currencyRows = currencies.data ?? [];
  const baseCurrency = currencyRows.find((c) => c.isBase)?.code ?? '';
  const foreign = !!currency && !!baseCurrency && currency !== baseCurrency;

  // §15 — the invoice is billed in the client's currency unless the setting
  // opens it up or the user may edit exchange rates. The server enforces the
  // same rule; this only keeps the form from offering what it would refuse.
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, Record<string, unknown>>>('/settings'),
  });
  const mayChooseCurrency =
    Boolean(settings.data?.finance?.['finance.allowInvoiceCurrencyOverride']) ||
    can(P.FINANCE_EXCHANGE_RATE_EDIT);

  // §14 — the project picker is not loaded until a client is chosen, and then
  // it only ever contains that client's projects.
  const projects = useQuery({
    queryKey: ['project-options', clientId],
    queryFn: () => api.get<ProjectOption[]>('/projects/options', { clientId }),
    enabled: !!clientId,
  });

  // Changing the client invalidates whatever project was picked (a contract's stays the contract's).
  const [lastClient, setLastClient] = useState(clientId);
  if (lastClient !== clientId) {
    setLastClient(clientId);
    if (!forContract && searchParams.get('projectId') !== projectId) setProjectId('');
  }

  const subtotal = useMemo(
    () => items.reduce((sum, i) => sum + Math.round(i.quantity * i.unitPrice * 100) / 100, 0),
    [items],
  );
  const total = Math.round((subtotal - discountAmount + taxAmount) * 100) / 100;

  const create = useMutation({
    mutationFn: () =>
      api.post<any>('/invoices', {
        clientId,
        issueDate,
        dueDate,
        items: items.map((i) => ({ description: i.description, quantity: i.quantity, unitPrice: i.unitPrice })),
        discountAmount: discountAmount || undefined,
        taxAmount: taxAmount || undefined,
        notes: notes || undefined,
        // A contract's period: its project and currency are the contract's, set by the API.
        ...(forContract
          ? { contractId, contractPeriodStart: period }
          : { projectId: projectId || undefined, currency: currency || undefined }),
      }),
    onSuccess: (invoice) => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      if (forContract) {
        void queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
        void queryClient.invalidateQueries({ queryKey: ['contracts'] });
      }
      navigate(`/invoices/${invoice.id}`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const valid =
    !!clientId &&
    (!forContract || !!draft.data) &&
    items.length > 0 &&
    items.every((i) => i.description.trim() && i.quantity > 0 && i.unitPrice >= 0);

  const fromContract = draft.data;
  return (
    <FormPage
      title={forContract ? t('invoices.newForContract') : t('invoices.new')}
      backTo={forContract ? `/contracts/${contractId}` : '/invoices'}
      backLabel={forContract ? (fromContract?.contract.number ?? t('nav.contracts')) : t('invoices.title')}
      width="lg"
      submitLabel={t('common.create')}
      submitting={create.isPending}
      disabled={!valid}
      loading={forContract && draft.isLoading}
      error={forContract ? draft.error : undefined}
      onRetry={() => void draft.refetch()}
      onSubmit={() => create.mutate()}
      summary={
        fromContract && (
          <>
            <dl className="grid gap-x-6 sm:grid-cols-2">
              <Detail label={t('invoices.forContract')}>
                <Link
                  to={`/contracts/${fromContract.contract.id}`}
                  className="font-mono text-mono-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {fromContract.contract.number}
                </Link>{' '}
                <span dir="auto">{fromContract.contract.title}</span>
              </Detail>
              <Detail label={t('invoices.contractPeriod')}>
                {formatDate(fromContract.period.start, locale)} – {formatDate(fromContract.period.end, locale)}
              </Detail>
              <Detail label={t('invoices.suggestedAmount')}>
                {formatMoney(fromContract.period.amount, fromContract.currency, locale)}
              </Detail>
              <Detail label={t('contracts.billing')}>
                {t(`contracts.billing.${fromContract.contract.billing}` as TranslationKey)}
              </Detail>
            </dl>
            <p className="mt-2 text-caption text-muted">{t('invoices.contractHint')}</p>
            {fromContract.clientInactive && (
              <p className="mt-2 text-caption text-warning-fg dark:text-warning-dark">{t('contracts.clientInactive')}</p>
            )}
          </>
        )
      }
    >
      <FormGrid>
        {/* §46 — client first: everything below depends on it. */}
        <Field label={t('invoices.client')} required hint={forContract ? t('invoices.fromTheContract') : undefined}>
          <Select value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={forContract} autoFocus={!forContract}>
            <option value="">—</option>
            {(clients.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {locale === 'ar' && c.companyNameAr ? c.companyNameAr : c.companyName}
              </option>
            ))}
          </Select>
        </Field>

        {forContract ? (
          // The contract's project, finished or not — not one of the picker's open projects.
          <Field label={t('invoices.project')} hint={t('invoices.fromTheContract')}>
            <Input
              value={fromContract?.project ? `${fromContract.project.code} — ${localized(fromContract.project)}` : t('invoices.noProject')}
              readOnly
              disabled
            />
          </Field>
        ) : (
          <Field
            label={t('invoices.project')}
            hint={clientId ? t('invoices.projectHint') : t('invoices.pickClientFirst')}
          >
            <Select value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={!clientId}>
              <option value="">{t('invoices.noProject')}</option>
              {(projects.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>{p.code} — {localized(p)}</option>
              ))}
            </Select>
          </Field>
        )}

        <Field label={t('invoices.issueDate')} required>
          <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
        </Field>
        <Field label={t('invoices.dueDate')} required>
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </Field>

        <Field
          label={t('common.currency')}
          hint={
            forContract
              ? t('invoices.fromTheContract')
              : client && currency !== client.defaultCurrency
                ? t('invoices.currencyDiffers', { currency: client.defaultCurrency })
                : client
                  ? t('clients.currencyHint')
                  : undefined
          }
        >
          {mayChooseCurrency && !forContract ? (
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)} dir="ltr">
              {currencyRows.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} — {localized(c)}
                </option>
              ))}
            </Select>
          ) : (
            <Input value={currency} readOnly disabled dir="ltr" />
          )}
        </Field>

        {/* Where the rate used to be asked for: it is the payment's now. */}
        {foreign ? (
          <Field label={t('payments.exchangeRate')}>
            <span className="block py-2.5 text-body-sm text-muted">
              {t('invoices.rateOnPayment', { base: baseCurrency })}
            </span>
          </Field>
        ) : (
          <div />
        )}

        <div className="sm:col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-caption uppercase text-[var(--text-tertiary)]">{t('invoices.items')}</p>
            <Button
              icon={<Plus className="h-4 w-4" />}
              onClick={() => setItems([...items, { description: '', quantity: 1, unitPrice: 0 }])}
            >
              {t('invoices.addItem')}
            </Button>
          </div>
          <div className="grid gap-2">
            {items.map((item, index) => (
              <div key={index} className="grid grid-cols-[1fr_5rem_7rem_7rem_2.5rem] items-center gap-2">
                <Input
                  placeholder={t('invoices.itemDescription')}
                  value={item.description}
                  onChange={(e) =>
                    setItems(items.map((it, i) => (i === index ? { ...it, description: e.target.value } : it)))
                  }
                />
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  value={item.quantity}
                  onChange={(e) =>
                    setItems(items.map((it, i) => (i === index ? { ...it, quantity: Number(e.target.value) } : it)))
                  }
                />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={item.unitPrice}
                  onChange={(e) =>
                    setItems(items.map((it, i) => (i === index ? { ...it, unitPrice: Number(e.target.value) } : it)))
                  }
                />
                <span className="text-end text-body-sm tabular text-muted">
                  {formatMoney(item.quantity * item.unitPrice, currency || 'EGP', locale)}
                </span>
                <Button
                  variant="danger"
                  icon={<Trash2 className="h-4 w-4" />}
                  disabled={items.length === 1}
                  onClick={() => setItems(items.filter((_, i) => i !== index))}
                />
              </div>
            ))}
          </div>
        </div>

        <Field label={t('invoices.discount')}>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={discountAmount}
            onChange={(e) => setDiscountAmount(Number(e.target.value))}
          />
        </Field>
        <Field label={t('invoices.tax')}>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={taxAmount}
            onChange={(e) => setTaxAmount(Number(e.target.value))}
          />
        </Field>

        <div className="rounded-lg border hairline p-4 sm:col-span-2">
          <dl className="divide-y divide-[var(--line)]">
            <Detail label={t('invoices.subtotal')}>{formatMoney(subtotal, currency || 'EGP', locale)}</Detail>
            <Detail label={t('common.total')}>
              <strong className="text-metric tabular">{formatMoney(total, currency || 'EGP', locale)}</strong>
            </Detail>
          </dl>
        </div>

        <Field label={t('common.notes')} className="sm:col-span-2">
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/**
 * §20, §48 — recording a payment. Everything above the inputs comes from the
 * server's payment context, so the form cannot disagree with the invoice.
 * The transfer receipt is optional; it uploads as soon as it is picked, and
 * the payment waits for it rather than being saved without it.
 *
 * Route: `/invoices/:id/payments/new`.
 */
export function PaymentFormPage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const context = useQuery({
    queryKey: ['payment-context', id],
    queryFn: () => api.get<any>(`/invoices/${id}/payment-context`),
  });

  const [form, setForm] = useState({
    amount: 0,
    paymentDate: isoDate(),
    paymentMethod: 'BANK_TRANSFER',
    exchangeRate: '' as number | '',
    exchangeRateSource: 'MANUAL',
    reference: '',
    notes: '',
  });
  const receipt = useReceiptUpload();

  // Default to clearing the balance — the common case — at the rate kept in
  // Settings > Currencies. Both are only a starting point: what gets stored is
  // whatever the operator confirms. With no rate kept, the field starts empty
  // rather than at 1, which would book a foreign payment as if it were local.
  const [seeded, setSeeded] = useState(false);
  if (context.data && !seeded) {
    setSeeded(true);
    setForm((f) => ({
      ...f,
      amount: context.data.invoice.remainingAmount,
      exchangeRate: context.data.suggestedExchangeRate ?? '',
    }));
  }

  const save = useMutation({
    mutationFn: () =>
      api.post(`/invoices/${id}/payments`, {
        amount: form.amount,
        paymentDate: form.paymentDate,
        paymentMethod: form.paymentMethod,
        exchangeRate: context.data?.exchangeRateRequired ? Number(form.exchangeRate) : undefined,
        exchangeRateSource: form.exchangeRateSource,
        reference: form.reference || undefined,
        notes: form.notes || undefined,
        attachmentId: receipt.file?.id,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['payments'] });
      void queryClient.invalidateQueries({ queryKey: ['client-payments'] });
      navigate(`/invoices/${id}`);
    },
    onError: (e: any) => {
      if (e?.details?.field === 'attachmentId' || e?.code === 'FILE_NOT_ATTACHABLE') {
        return toast.error(t('payments.transferReceiptRefused', { reason: e.message }));
      }
      toast.error(e.message);
    },
  });

  const ctx = context.data;
  const rateMissing = !!ctx?.exchangeRateRequired && !(Number(form.exchangeRate) > 0);
  const localAmount = !ctx || rateMissing ? null : form.amount * (ctx.exchangeRateRequired ? Number(form.exchangeRate) : 1);

  return (
    <FormPage
      title={t('invoices.recordPayment')}
      backTo={`/invoices/${id}`}
      backLabel={ctx?.invoice.number ?? t('invoices.title')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={!ctx || form.amount <= 0 || form.amount > ctx.invoice.remainingAmount || rateMissing || receipt.busy}
      loading={context.isLoading}
      error={context.error}
      onRetry={() => void context.refetch()}
      onSubmit={() => save.mutate()}
      summary={
        ctx && (
          <dl className="grid gap-x-6 sm:grid-cols-2">
            <Detail label={t('invoices.client')}>{ctx.client.companyName}</Detail>
            <Detail label={t('tickets.number')}>
              <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{ctx.invoice.number}</span>
            </Detail>
            <Detail label={t('payments.invoiceTotal')}>
              {formatMoney(ctx.invoice.totalAmount, ctx.invoice.currency, locale)}
            </Detail>
            <Detail label={t('payments.alreadyPaid')}>
              {formatMoney(ctx.invoice.paidAmount, ctx.invoice.currency, locale)}
            </Detail>
            <Detail label={t('invoices.remaining')}>
              <strong className="text-warning-fg dark:text-warning-dark">
                {formatMoney(ctx.invoice.remainingAmount, ctx.invoice.currency, locale)}
              </strong>
            </Detail>
            <Detail label={t('common.currency')}>
              <span className="font-mono">{ctx.invoice.currency}</span>
            </Detail>
          </dl>
        )
      }
    >
      {ctx && (
        <FormGrid>
          <Field label={t('common.amount')} required>
            <Input
              type="number"
              step="0.01"
              max={ctx.invoice.remainingAmount}
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
              autoFocus
            />
          </Field>
          <Field label={t('common.date')} required>
            <Input
              type="date"
              value={form.paymentDate}
              onChange={(e) => setForm({ ...form, paymentDate: e.target.value })}
            />
          </Field>

          {/* §26 — a foreign-currency payment needs the day's rate; a payment
              already in the base currency converts at 1 and asks nothing. The
              invoice has no rate of its own, so this is the only place it is
              entered, once per payment. */}
          {ctx.exchangeRateRequired && (
            <>
              <Field
                label={t('payments.rateOn', {
                  base: ctx.baseCurrency,
                  // Local midnight, so the day shown is the day picked in any time zone.
                  date: formatDate(`${form.paymentDate}T00:00:00`, locale),
                })}
                required
                hint={
                  <>
                    <bdi dir="ltr" className="font-mono">
                      1 {ctx.invoice.currency} = ? {ctx.baseCurrency}
                    </bdi>
                    {' · '}
                    {t('payments.rateOnHint')}
                  </>
                }
              >
                <Input
                  type="number"
                  step="0.00000001"
                  min="0"
                  dir="ltr"
                  value={form.exchangeRate}
                  onChange={(e) =>
                    setForm({ ...form, exchangeRate: e.target.value === '' ? '' : Number(e.target.value) })
                  }
                />
              </Field>
              <Field label={t('payments.exchangeRateSource')}>
                <Select
                  value={form.exchangeRateSource}
                  onChange={(e) => setForm({ ...form, exchangeRateSource: e.target.value })}
                >
                  {['MANUAL', 'BANK', 'EXCHANGE_OFFICE', 'OTHER'].map((s) => (
                    <option key={s} value={s}>{enumLabel(s, locale)}</option>
                  ))}
                </Select>
              </Field>
            </>
          )}

          <Field label={t('payments.method')} required>
            <Select value={form.paymentMethod} onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}>
              {(ctx.paymentMethods ?? []).map((m: string) => (
                <option key={m} value={m}>{m.replace('_', ' ')}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('payments.reference')}>
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
          </Field>

          <Field label={t('payments.localAmount')} className="sm:col-span-2">
            <span className="block text-metric tabular text-[var(--text-primary)]">
              {localAmount === null ? '—' : formatMoney(localAmount, ctx.baseCurrency, locale)}
            </span>
          </Field>

          <Field label={t('common.notes')} className="sm:col-span-2">
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>

          <ReceiptPicker receipt={receipt} className="sm:col-span-2" />
        </FormGrid>
      )}
    </FormPage>
  );
}

/**
 * A discount after the invoice is out (operator, 2026-10-12: "after issue an
 * invoice i can add discount only in same currency and payment and everything
 * only discount") — the one change an issued invoice takes. An amount in the
 * invoice's own currency, replacing the discount it has; payments may be in
 * already. The API's checks run here as it is typed, so Save is offered only for
 * what the API takes, and the new total and balance show before saving.
 *
 * Route: `/invoices/:id/discount`.
 */
export function InvoiceDiscountPage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const invoice = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => api.get<any>(`/invoices/${id}`),
  });

  // Starts at the discount the invoice has; what is typed replaces it.
  const [amount, setAmount] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [seeded, setSeeded] = useState(false);
  if (invoice.data && !seeded) {
    setSeeded(true);
    setAmount(Number(invoice.data.discountAmount) || '');
  }

  const save = useMutation({
    mutationFn: () =>
      api.post(`/invoices/${id}/discount`, { discountAmount: Number(amount || 0), reason: reason.trim() || undefined }),
    onSuccess: () => {
      // The total moves, and much of Finance shows it — the client's money, the
      // project's, the dashboard, the reports — so all of it is fetched afresh.
      void queryClient.invalidateQueries();
      navigate(`/invoices/${id}`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const inv = invoice.data;
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const money = (n: number) => formatMoney(n, inv?.currency, locale);
  const subtotal = Number(inv?.subtotal ?? 0);
  const tax = Number(inv?.taxAmount ?? 0);
  const paid = Number(inv?.paidAmount ?? 0);
  const current = Number(inv?.discountAmount ?? 0);
  const discount = round2(Number(amount || 0));
  const total = round2(subtotal - discount + tax);
  const remaining = round2(total - paid);
  const cancelled = inv?.status === 'CANCELLED';

  const problem = !inv
    ? null
    : cancelled
      ? t('invoices.discountCancelled')
      : discount < 0
        ? t('invoices.discountNegative')
        : discount > subtotal
          ? t('invoices.discountOverSubtotal', { amount: money(subtotal) })
          : total < paid
            ? t('invoices.discountBelowPaid', { paid: money(paid), max: money(round2(subtotal + tax - paid)) })
            : total <= 0
              ? t('invoices.discountWholeInvoice')
              : null;

  return (
    <FormPage
      title={current > 0 ? t('invoices.changeDiscount') : t('invoices.addDiscount')}
      backTo={`/invoices/${id}`}
      backLabel={inv?.number ?? t('invoices.title')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={!inv || !!problem || discount === round2(current)}
      loading={invoice.isLoading}
      error={invoice.error}
      onRetry={() => void invoice.refetch()}
      onSubmit={() => save.mutate()}
      summary={
        inv && (
          <dl className="grid gap-x-6 sm:grid-cols-2">
            <Detail label={t('invoices.client')}>
              {locale === 'ar' && inv.client.companyNameAr ? inv.client.companyNameAr : inv.client.companyName}
            </Detail>
            <Detail label={t('tickets.number')}>
              <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{inv.number}</span>
            </Detail>
            <Detail label={t('common.status')}>
              <Badge tone={statusTone(inv.status)}>{enumLabel(inv.status, locale)}</Badge>
            </Detail>
            <Detail label={t('common.currency')}>
              <span className="font-mono">{inv.currency}</span>
            </Detail>
          </dl>
        )
      }
    >
      {inv && (
        <FormGrid>
          <Field
            label={t('invoices.discountIn', { currency: inv.currency })}
            required
            error={problem}
            hint={t('invoices.discountHint', {
              share: subtotal > 0 ? formatPercent((discount / subtotal) * 100, locale) : '—',
              currency: inv.currency,
            })}
          >
            <Input
              type="number"
              step="0.01"
              min="0"
              max={subtotal}
              value={amount}
              disabled={cancelled}
              onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
              autoFocus
            />
          </Field>
          <Field label={t('invoices.discountReason')} hint={t('invoices.discountReasonHint')}>
            <Input value={reason} disabled={cancelled} onChange={(e) => setReason(e.target.value)} />
          </Field>

          {/* What the invoice will say: the same lines as its Details card and its PDF. */}
          <div className="rounded-lg border hairline p-4 sm:col-span-2">
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('invoices.subtotal')}>{money(subtotal)}</Detail>
              <Detail label={t('invoices.discount')}>{discount > 0 ? `−${money(discount)}` : money(0)}</Detail>
              <Detail label={t('invoices.tax')}>{money(tax)}</Detail>
              <Detail label={t('common.total')}>
                <span className="flex flex-col items-end">
                  <strong className="text-metric tabular">{money(total)}</strong>
                  {total !== Number(inv.totalAmount) && (
                    <span className="text-caption text-muted">
                      {t('invoices.totalWas', { amount: money(Number(inv.totalAmount)) })}
                    </span>
                  )}
                </span>
              </Detail>
              <Detail label={t('payments.alreadyPaid')}>{money(paid)}</Detail>
              <Detail label={t('invoices.remaining')}>
                <strong className={remaining > 0 ? 'text-warning-fg dark:text-warning-dark' : 'text-success-fg dark:text-success'}>{money(Math.max(remaining, 0))}</strong>
              </Detail>
            </dl>
          </div>
        </FormGrid>
      )}
    </FormPage>
  );
}

/**
 * §61 cancellation and §60 reversal. Both take a reason, so both are pages.
 *
 * Routes: `/invoices/:id/cancel` and `/payments/:paymentId/reverse`.
 */
export function InvoiceCancelPage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const invoice = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => api.get<any>(`/invoices/${id}`),
  });

  const cancel = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/cancel`, { reason: reason || undefined }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate(`/invoices/${id}`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('invoices.cancel')}
      backTo={`/invoices/${id}`}
      backLabel={invoice.data?.number ?? t('invoices.title')}
      submitLabel={t('common.confirm')}
      destructive
      submitting={cancel.isPending}
      loading={invoice.isLoading}
      error={invoice.error}
      onRetry={() => void invoice.refetch()}
      onSubmit={() => cancel.mutate()}
      summary={
        invoice.data && (
          <dl className="grid gap-x-6 sm:grid-cols-2">
            <Detail label={t('tickets.number')}>
              <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{invoice.data.number}</span>
            </Detail>
            <Detail label={t('invoices.client')}>{invoice.data.client.companyName}</Detail>
            <Detail label={t('common.total')}>
              {formatMoney(invoice.data.totalAmount, invoice.data.currency, locale)}
            </Detail>
            <Detail label={t('invoices.paid')}>
              {formatMoney(invoice.data.paidAmount, invoice.data.currency, locale)}
            </Detail>
          </dl>
        )
      }
    >
      <Field label={t('invoices.cancelReason')}>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </FormPage>
  );
}

export function PaymentReversePage() {
  const { paymentId = '' } = useParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const payment = useQuery({
    queryKey: ['payment', paymentId],
    queryFn: () => api.get<any>(`/payments/${paymentId}`),
  });

  const reverse = useMutation({
    mutationFn: () => api.post(`/payments/${paymentId}/reverse`, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice'] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['payments'] });
      navigate(payment.data ? `/invoices/${payment.data.invoice.id}` : '/payments');
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('payments.reverse')}
      backTo={payment.data ? `/invoices/${payment.data.invoice.id}` : '/payments'}
      backLabel={payment.data?.invoice.number ?? t('payments.title')}
      submitLabel={t('common.confirm')}
      destructive
      submitting={reverse.isPending}
      disabled={!reason.trim()}
      loading={payment.isLoading}
      error={payment.error}
      onRetry={() => void payment.refetch()}
      onSubmit={() => reverse.mutate()}
      summary={
        payment.data && (
          <dl className="grid gap-x-6 sm:grid-cols-2">
            <Detail label={t('payments.number')}>
              <span className="font-mono text-mono-sm">{payment.data.number}</span>
            </Detail>
            <Detail label={t('nav.invoices')}>
              <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{payment.data.invoice.number}</span>
            </Detail>
            <Detail label={t('common.amount')}>
              {formatMoney(payment.data.amount, payment.data.currency, locale)}
            </Detail>
            <Detail label={t('payments.localAmount')}>
              {formatMoney(payment.data.localAmount, payment.data.localCurrency, locale)}
            </Detail>
          </dl>
        )
      }
    >
      <Field label={t('payments.reverseReason')} required>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </FormPage>
  );
}
